export type Style = "APA" | "MLA" | "Chicago" | "Harvard";
export type SourceType = "Website" | "Book" | "Journal Article" | "Book Chapter";
export type ChicagoEdition = "17" | "18";
export type Seg = { t: string; i?: boolean };

export interface Author { last: string; first: string }

export interface Fields {
  authorLast: string; authorFirst: string; extraAuthors?: Author[]; // authorLast/First is author 1; extraAuthors are 2, 3, ...
  title: string; year: string; pubDate: string; // pubDate: yyyy-mm-dd, optional
  siteName: string; publisher: string; city: string; url: string; accessDate: string; // accessDate: yyyy-mm-dd, blank = today
  journalName: string; volume: string; issue: string; pages: string;
  /** Optional extras (the website form does not send them): a DOI, the edition of a book, and for a chapter the book title and its editors. */
  doi?: string; edition?: string; bookTitle?: string; editors?: Author[];
  /** A URL for a book, article or chapter read online. (`url` is only used for a website; the form keeps one URL box for all types.) */
  sourceUrl?: string;
  /** Chicago only. "17" (default) keeps the publisher city; "18" (2024) leaves it out for books published since 1900. */
  chicagoEdition?: ChicagoEdition;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MLA_MONTHS = ["Jan.", "Feb.", "Mar.", "Apr.", "May", "June", "July", "Aug.", "Sept.", "Oct.", "Nov.", "Dec."];

export function parseIso(s: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  return m ? { y: +m[1], m: +m[2] - 1, d: +m[3] } : null;
}
export function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const fmtDMY = (s: string) => { const p = parseIso(s); return p ? `${p.d} ${MONTHS[p.m]} ${p.y}` : s; };
const fmtMDY = (s: string) => { const p = parseIso(s); return p ? `${MONTHS[p.m]} ${p.d}, ${p.y}` : s; };
const fmtMLA = (s: string) => { const p = parseIso(s); return p ? `${p.d} ${MLA_MONTHS[p.m]} ${p.y}` : s; };

/** "Jane Marie" → "J. M."; "Jean-Paul" → "J.-P." */
export function initials(first: string): string {
  return first.trim().split(/\s+/).filter(Boolean).map((n) => n.split("-").map((x) => `${x[0].toUpperCase()}.`).join("-")).join(" ");
}
const dash = (pages: string) => pages.trim().replace(/(\d)\s*-\s*(\d)/g, "$1–$2");
const RANGE = /^(\d+)\s*[-–—]\s*(\d+)$/;
/** MLA: an en dash between page numbers (published tool wording, B-API c28); above 99 only the last two digits of the second number when only they change (225-50). */
export function mlaPages(pages: string): string {
  const t = pages.trim();
  const m = RANGE.exec(t);
  if (!m) return t;
  const [a, b] = [m[1], m[2]];
  if (a.length >= 3 && a.length === b.length && Number(b) > Number(a)) {
    let i = 0; while (i < a.length && a[i] === b[i]) i++;
    if (i >= a.length - 2) return `${a}–${b.slice(a.length - 2)}`;
  }
  return `${a}–${b}`;
}
/** Chicago 17/18 (9.61): 872-898 -> 872–98, 107-108 -> 107–8, 1496-1500 -> 1496–500, under 100 or a multiple of 100 in full. */
export function chicagoPages(pages: string): string {
  const t = pages.trim();
  const m = RANGE.exec(t);
  if (!m) return dash(t);
  const [a, b] = [m[1], m[2]];
  if (a.length !== b.length || Number(b) <= Number(a) || Number(a) < 100 || Number(a) % 100 === 0) return `${a}–${b}`;
  let i = 0; while (i < a.length && a[i] === b[i]) i++;
  const from = Number(a) % 100 < 10 ? i : Math.min(i, a.length - 2);
  return `${a}–${b.slice(from)}`;
}
const pageLabel = (pages: string, fmt: (p: string) => string) => (RANGE.test(pages.trim()) ? `pp. ${fmt(pages)}` : `p. ${pages.trim()}`);
/** A DOI as the https://doi.org/ link every current style asks for; a URL is left as it is. */
export function doiLink(doi: string): string {
  const t = doi.trim().replace(/^doi:\s*/i, "");
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t.replace(/^http:\/\//i, "https://").replace(/^https:\/\/(dx\.)?doi\.org\//i, "https://doi.org/");
  return /^10\.\d{4,9}\//.test(t) ? `https://doi.org/${t}` : t;
}
/** "2" -> "2nd ed."; "2nd" and "2nd ed." -> "2nd ed."; "Revised" -> "Revised ed.". */
export function editionText(raw: string): string {
  const t = raw.trim().replace(/\.$/, "").replace(/\s+(ed|edition)$/i, "");
  if (!t) return "";
  const n = /^(\d+)(?:st|nd|rd|th)?$/i.exec(t);
  if (!n) return `${t} ed.`;
  const v = Number(n[1]), tens = v % 100;
  const suf = tens >= 11 && tens <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[v % 10] ?? "th";
  return `${v}${suf} ed.`;
}
const sameText = (a: string, b: string) => a.toLowerCase().replace(/[^a-z0-9]/g, "") === b.toLowerCase().replace(/[^a-z0-9]/g, "") && a.trim() !== "";
const stripProtocol = (u: string) => u.trim().replace(/^https?:\/\//i, "").replace(/\/$/, "");
const endDot = (s: string) => (/[.!?]$/.test(s) ? s : s + ".");
const PARTICLES = new Set(["van", "von", "de", "der", "den", "del", "della", "di", "da", "du", "la", "le", "bin", "ibn", "al", "el", "ter", "ten"]);
/** "smith" → "Smith", "jean-paul" → "Jean-Paul"; words that already have a capital (McDonald) and name particles (van der) are left alone. */
export function capName(name: string): string {
  return name.trim().split(/\s+/).filter(Boolean).map((w, i) => {
    if (w !== w.toLowerCase()) return w;
    if (i > 0 && PARTICLES.has(w)) return w;
    return w.replace(/(^|[-'’])([a-zà-ÿ])/g, (_m, a: string, c: string) => a + c.toUpperCase());
  }).join(" ");
}
/** Adds https:// to a bare domain such as "bbc.com/x"; leaves full URLs and non-URLs alone. */
export function fullUrl(u: string): string {
  const t = u.trim();
  if (!t || /^[a-z][a-z0-9+.-]*:\/\//i.test(t) || /\s/.test(t)) return t;
  return /^[^/]+\.[a-z]{2,}(?::\d+)?(\/|$|\?|#)/i.test(t) ? `https://${t}` : t;
}
export const authorList = (f: Fields): Author[] =>
  [{ last: f.authorLast, first: f.authorFirst }, ...(f.extraAuthors ?? [])]
    .map((a) => ({ last: capName(a.last), first: capName(a.first) })).filter((a) => a.last);
const invFirst = (a: Author) => (a.first ? `${a.last}, ${a.first}` : a.last);

/** Author string for the reference list: surname-first name(s), joined the way each style requires. "" when there is no author. */
export function joinAuthors(style: Style, authors: Author[], chicagoEdition: ChicagoEdition = "17"): string {
  const n = authors.length;
  if (n === 0) return "";
  const ini = (a: Author) => (a.first ? `${a.last}, ${initials(a.first)}` : a.last);
  if (style === "APA") {
    const l = authors.map(ini);
    // APA 7: up to 20 authors are listed; with 21 or more, the first 19, an ellipsis, then the last author (no ampersand).
    if (n > 20) return `${l.slice(0, 19).join(", ")}, … ${l[n - 1]}`;
    return n === 1 ? l[0] : n === 2 ? `${l[0]}, & ${l[1]}` : `${l.slice(0, -1).join(", ")}, & ${l[n - 1]}`;
  }
  if (style === "Harvard") {
    const l = authors.map(ini);
    return n === 1 ? l[0] : `${l.slice(0, -1).join(", ")} and ${l[n - 1]}`;
  }
  if (style === "MLA") {
    if (n === 1) return invFirst(authors[0]);
    if (n === 2) return `${invFirst(authors[0])}, and ${authors[1].first ? `${authors[1].first} ` : ""}${authors[1].last}`;
    return `${invFirst(authors[0])}, et al.`;
  }
  // Chicago bibliography: 18th lists up to 6 authors (7 or more: the first 3 and et al.); 17th lists up to 10 (11 or more: the first 7 and et al.).
  const [max, keep] = chicagoEdition === "18" ? [6, 3] : [10, 7];
  const etAl = n > max;
  if (etAl) authors = authors.slice(0, keep);
  const rest = authors.slice(1).map((a) => (a.first ? `${a.first} ${a.last}` : a.last));
  if (etAl) return `${invFirst(authors[0])}, ${rest.join(", ")}, et al.`;
  if (n === 1) return invFirst(authors[0]);
  return n === 2 ? `${invFirst(authors[0])}, and ${rest[0]}` : `${invFirst(authors[0])}, ${rest.slice(0, -1).join(", ")}, and ${rest[n - 2]}`;
}
const T = (t: string, i = false): Seg => (i ? { t, i } : { t });

/** Editors for the reference list: APA "E. A. Editor & E. B. Editor", MLA/Chicago "First Last and First Last" (MLA: three or more is "First Last et al."). */
function editorNames(style: Style, eds: Author[]): string {
  const n = eds.length;
  if (!n) return "";
  if (style === "APA") {
    const l = eds.map((e) => (e.first ? `${initials(e.first)} ${e.last}` : e.last));
    return n === 1 ? l[0] : n === 2 ? `${l[0]} & ${l[1]}` : `${l.slice(0, -1).join(", ")}, & ${l[n - 1]}`;
  }
  const l = eds.map((e) => (e.first ? `${e.first} ${e.last}` : e.last));
  if (style === "MLA" && n >= 3) return `${l[0]} et al.`;
  return n === 1 ? l[0] : n === 2 ? `${l[0]} and ${l[1]}` : `${l.slice(0, -1).join(", ")}, and ${l[n - 1]}`;
}
const cleanEditors = (f: Fields): Author[] => (f.editors ?? []).map((a) => ({ last: capName(a.last), first: capName(a.first) })).filter((a) => a.last);

/** Builds a citation as styled segments (italics where the style requires them). Missing pieces are skipped rather than left as ", ,". */
export function buildCitation(style: Style, type: SourceType, f: Fields): Seg[] {
  const title = f.title.trim();
  const pub = parseIso(f.pubDate);
  const year = (pub ? String(pub.y) : f.year.trim()) || "";
  const access = f.accessDate.trim() || localToday();
  const url = fullUrl(f.url);
  const doi = doiLink(f.doi ?? "");
  const srcUrl = type === "Website" ? url : fullUrl(f.sourceUrl ?? "");
  const link = doi || srcUrl; // a DOI is used instead of a URL when there is one
  const authors = authorList(f);
  const editors = cleanEditors(f);
  const edition = editionText(f.edition ?? "");
  const bookTitle = (f.bookTitle ?? "").trim();
  const ch18 = f.chicagoEdition === "18";
  const out: Seg[] = [];
  const push = (...s: (Seg | null | "" | false | undefined | 0)[]) => s.forEach((x) => { if (x) out.push(x); });
  const has = (s: string) => s.trim() !== "";

  if (style === "APA") {
    const who = joinAuthors("APA", authors);
    const date = `(${year || "n.d."})`;
    const lead = (italicTitle: boolean, t: string) => {
      if (who) push(T(`${endDot(who)} ${date}. `), italicTitle ? T(endDot(t), true) : T(`${endDot(t)} `));
      else push(italicTitle ? T(endDot(t), true) : T(endDot(t)), T(` ${date}. `));
    };
    if (type === "Website") {
      const d = pub ? `(${pub.y}, ${MONTHS[pub.m]} ${pub.d})` : date;
      if (who) push(T(`${endDot(who)} ${d}. `), T(endDot(title), true));
      else push(T(endDot(title), true), T(` ${d}.`));
      // APA 7: the site name is left out when it is the same as the (group) author.
      push(has(f.siteName) && !sameText(f.siteName, who) && T(` ${endDot(f.siteName.trim())}`), has(link) && T(` ${link}`));
    } else if (type === "Book") {
      if (who) push(T(`${endDot(who)} ${date}. `));
      push(edition ? T(title, true) : T(endDot(title), true));
      push(edition && T(` (${edition}).`));
      if (!who) push(T(` ${date}.`));
      push(has(f.publisher) && T(` ${endDot(f.publisher.trim())}`), has(link) && T(` ${link}`));
    } else if (type === "Book Chapter") {
      lead(false, title);
      const eds = editorNames("APA", editors);
      const paren = [edition, has(f.pages) ? pageLabel(f.pages, dash) : ""].filter(Boolean).join(", ");
      push(T(`In ${eds ? `${eds} (${editors.length > 1 ? "Eds." : "Ed."}), ` : ""}`), T(bookTitle, true), paren && T(` (${paren})`), T("."));
      push(has(f.publisher) && T(` ${endDot(f.publisher.trim())}`), has(link) && T(` ${link}`));
    } else {
      if (who) push(T(`${endDot(who)} ${date}. ${endDot(title)} `)); else push(T(`${endDot(title)} ${date}. `));
      push(T(f.journalName.trim(), true));
      push(has(f.volume) && T(", "), has(f.volume) && T(f.volume.trim(), true), has(f.issue) && T(`(${f.issue.trim()})`), has(f.pages) && T(`, ${dash(f.pages)}`), T("."));
      push(has(link) && T(` ${link}`));
    }
  } else if (style === "MLA") {
    const who = joinAuthors("MLA", authors);
    const lead = who ? `${endDot(who)} ` : "";
    // Everything after the title is comma-separated and ends with one period (MLA 9 core elements).
    const tail = (parts: (string | false | undefined)[]) => { const p = parts.filter(Boolean) as string[]; return p.length ? `${p.join(", ")}.` : "."; };
    const mlaLink = doi || (has(srcUrl) ? stripProtocol(srcUrl) : "");
    if (type === "Website") {
      push(T(`${lead}"${endDot(title)}" `));
      const date = pub ? fmtMLA(f.pubDate) : year;
      const rest = [date, mlaLink].filter(Boolean).join(", ");
      if (has(f.siteName)) push(T(f.siteName.trim(), true), T(rest ? `, ${rest}.` : "."));
      else push(T(rest ? `${rest}.` : ""));
      // The access date is optional in MLA 9: given when the person typed one, or when the page has no date.
      if (has(f.accessDate) || !(pub || year)) push(T(` Accessed ${fmtMLA(access)}.`));
    } else if (type === "Book") {
      push(T(lead), T(endDot(title), true), T(` ${tail([edition, has(f.publisher) && f.publisher.trim(), year, mlaLink])}`));
    } else if (type === "Book Chapter") {
      push(T(`${lead}"${endDot(title)}" `), T(bookTitle, true));
      push(T(`, ${tail([edition, editors.length ? `edited by ${editorNames("MLA", editors)}` : "", has(f.publisher) && f.publisher.trim(), year, has(f.pages) && pageLabel(f.pages, mlaPages), mlaLink])}`));
    } else {
      push(T(`${lead}"${endDot(title)}" `), T(f.journalName.trim(), true));
      push(T(`, ${tail([has(f.volume) && `vol. ${f.volume.trim()}`, has(f.issue) && `no. ${f.issue.trim()}`, year, has(f.pages) && pageLabel(f.pages, mlaPages), mlaLink])}`));
    }
  } else if (style === "Chicago") {
    // No author but a site name: the site takes the author's place (Chicago 18 handout: Name of Website. "Title of Webpage." Date. URL.).
    const siteAsAuthor = type === "Website" && !authors.length && has(f.siteName);
    const who = joinAuthors("Chicago", authors, f.chicagoEdition ?? "17");
    const lead = who ? `${endDot(who)} ` : siteAsAuthor ? `${endDot(f.siteName.trim())} ` : "";
    const pubLine = (withCity: boolean) => {
      const place = withCity && !ch18 && has(f.city) ? `${f.city.trim()}: ` : "";
      const body = [has(f.publisher) && f.publisher.trim(), year].filter(Boolean).join(", ");
      return body ? `${place}${body}.` : "";
    };
    const linkEnd = has(link) ? ` ${link}.` : "";
    if (type === "Website") {
      push(T(`${lead}"${endDot(title)}" `));
      push(has(f.siteName) && !siteAsAuthor && T(`${endDot(f.siteName.trim())} `));
      // Chicago 17 (14.206) and 18: the publication date when there is one (full date, or just the year); "Accessed" only for undated pages.
      push(pub ? T(`${fmtMDY(f.pubDate)}. `) : year ? T(`${year}. `) : T(`Accessed ${fmtMDY(access)}. `), has(url) && T(`${url}.`));
    } else if (type === "Book") {
      push(T(lead), T(endDot(title), true), edition && T(` ${edition}`), T(" "), T(pubLine(true).trim()), T(linkEnd));
    } else if (type === "Book Chapter") {
      const eds = editorNames("Chicago", editors);
      // 17th: the chapter's page range goes in the bibliography entry; 18th: no longer required.
      const pg = !ch18 && has(f.pages) ? `, ${chicagoPages(f.pages)}` : "";
      push(T(`${lead}"${endDot(title)}" In `), T(bookTitle, true), T(`${eds ? `, edited by ${eds}` : ""}${pg}. `), T(pubLine(true).trim()), T(linkEnd));
    } else {
      push(T(`${lead}"${endDot(title)}" `), T(f.journalName.trim(), true));
      push(has(f.volume) && T(` ${f.volume.trim()}`), has(f.issue) && T(`, no. ${f.issue.trim()}`), year && T(` (${year})`), has(f.pages) && T(`: ${chicagoPages(f.pages)}`), T("."), has(link) && T(` ${link}.`));
    }
  } else {
    const who = joinAuthors("Harvard", authors);
    const yr = `(${year || "n.d."})`;
    const lead = who ? `${who} ${yr} ` : "";
    if (type === "Website") {
      if (who) push(T(lead), T(endDot(title), true)); else push(T(endDot(title), true), T(` ${yr}`));
      push(has(url) && T(` Available at: ${url}`), T(` (Accessed: ${fmtDMY(access)}).`));
    } else if (type === "Book") {
      if (who) push(T(lead), T(endDot(title), true)); else push(T(endDot(title), true), T(` ${yr}`));
      push(T(" "), has(f.city) && T(`${f.city.trim()}: `), has(f.publisher) && T(`${endDot(f.publisher.trim())}`));
    } else if (type === "Book Chapter") {
      const eds = editors.map((e) => (e.first ? `${initials(e.first)} ${e.last}` : e.last));
      push(T(`${lead || `${title} ${yr} `}'${title}', in `), eds.length ? T(`${eds.join(" and ")} (${eds.length > 1 ? "eds." : "ed."}) `) : "", T(bookTitle, true));
      push(has(f.city) && T(`. ${f.city.trim()}`), has(f.publisher) && T(`${has(f.city) ? ": " : ". "}${f.publisher.trim()}`), has(f.pages) && T(`, ${pageLabel(f.pages, dash)}`), T("."));
    } else {
      push(T(`${lead}'${title}', `), T(f.journalName.trim(), true));
      push(has(f.volume) && T(`, ${f.volume.trim()}`), has(f.issue) && T(`(${f.issue.trim()})`), has(f.pages) && T(`, pp. ${dash(f.pages)}`), T("."));
    }
  }
  return out.reduce<Seg[]>((acc, s) => { const prev = acc[acc.length - 1]; if (prev && !prev.i === !s.i) prev.t += s.t; else acc.push({ ...s }); return acc; }, [])
    .map((s) => ({ ...s, t: s.t.replace(/\s+/g, " ").replace(/\s+([,.])/g, "$1").replace(/\.\./g, ".") }));
}

export const plain = (segs: Seg[]) => segs.map((s) => s.t).join("").trim();
const esc = (t: string) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export const toHtml = (segs: Seg[]) => segs.map((s) => (s.i ? `<i>${esc(s.t)}</i>` : esc(s.t))).join("").trim();

// ---------------------------------------------------------------------------------------------------------------
// In-text citations (APA, MLA), Chicago notes, and capitalization advice.

const joinNames = (names: string[], style: "amp" | "and") => (names.length === 1 ? names[0] : names.length === 2 ? `${names[0]} ${style === "amp" ? "&" : "and"} ${names[1]}` : `${names[0]} et al.`);

/** What goes in the text next to a quotation or idea: APA (Author, Year) and Author (Year); MLA (Author page). Chicago uses notes: see buildNote. */
export function buildInText(style: "APA" | "MLA", type: SourceType, f: Fields, page = ""): { parenthetical: string; narrative?: string } {
  const authors = authorList(f);
  const names = authors.map((a) => a.last);
  const pub = parseIso(f.pubDate);
  const year = (pub ? String(pub.y) : f.year.trim()) || "n.d.";
  const pg = page.trim();
  const quoted = type === "Journal Article" || type === "Book Chapter" || type === "Website";
  const shortTitle = `${quoted ? "“" : ""}${f.title.trim().replace(/[.!?]+$/, "")}${quoted ? "”" : ""}`;
  if (style === "APA") {
    const loc = pg ? `, ${RANGE.test(pg) ? `pp. ${dash(pg)}` : `p. ${pg}`}` : "";
    const who = names.length ? joinNames(names, "amp") : shortTitle;
    return { parenthetical: `(${who}, ${year}${loc})`, narrative: names.length ? `${joinNames(names, "and")} (${year}${loc})` : `${shortTitle} (${year}${loc})` };
  }
  const who = names.length ? joinNames(names, "and") : shortTitle;
  return { parenthetical: `(${who}${pg ? ` ${mlaPages(pg)}` : ""})` };
}

/** Chicago notes-bibliography: the first full note for a source (Turabian 9 / Chicago 17 forms, or the 18th without publisher city). */
export function buildNote(type: SourceType, f: Fields, page = ""): Seg[] {
  const ch18 = f.chicagoEdition === "18";
  const authors = authorList(f);
  const natural = (a: Author) => (a.first ? `${a.first} ${a.last}` : a.last);
  const who = authors.length === 0 ? "" : authors.length === 1 ? natural(authors[0]) : authors.length === 2 ? `${natural(authors[0])} and ${natural(authors[1])}` : authors.length === 3 ? `${natural(authors[0])}, ${natural(authors[1])}, and ${natural(authors[2])}` : `${natural(authors[0])} et al.`;
  const pub = parseIso(f.pubDate);
  const year = (pub ? String(pub.y) : f.year.trim()) || "";
  const title = f.title.trim().replace(/[.,]+$/, "");
  const pg = page.trim() ? chicagoPages(page) : "";
  const edition = editionText(f.edition ?? "");
  const doi = doiLink(f.doi ?? "");
  const link = doi || (type === "Website" ? fullUrl(f.url) : fullUrl(f.sourceUrl ?? ""));
  const place = !ch18 && f.city.trim() ? `${f.city.trim()}: ` : "";
  const pubParen = `(${place}${[f.publisher.trim(), year].filter(Boolean).join(", ")})`;
  const out: Seg[] = [];
  const t = (x: string, i = false) => out.push(T(x, i));
  if (who) t(`${who}, `);
  if (type === "Book") {
    t(title, true); if (edition) t(`, ${edition}`); t(` ${pubParen}`); if (pg) t(`, ${pg}`); if (link) t(`, ${link}`);
  } else if (type === "Book Chapter") {
    const eds = editorNames("Chicago", cleanEditors(f));
    t(`“${title},” in `); t((f.bookTitle ?? "").trim(), true); if (eds) t(`, ed. ${eds}`); t(` ${pubParen}`); if (pg) t(`, ${pg}`); if (link) t(`, ${link}`);
  } else if (type === "Journal Article") {
    t(`“${title},” `); t(f.journalName.trim(), true);
    if (f.volume.trim()) t(` ${f.volume.trim()}`); if (f.issue.trim()) t(`, no. ${f.issue.trim()}`); if (year) t(` (${year})`);
    if (pg) t(`: ${pg}`); if (link) t(`, ${link}`);
  } else {
    t(`“${title},”`);
    const bits = [f.siteName.trim(), pub ? fmtMDY(f.pubDate) : year || `accessed ${fmtMDY(f.accessDate.trim() || localToday())}`, link].filter(Boolean);
    t(` ${bits.join(", ")}`);
  }
  t(".");
  return out.reduce<Seg[]>((acc, s) => { const prev = acc[acc.length - 1]; if (prev && !prev.i === !s.i) prev.t += s.t; else acc.push({ ...s }); return acc; }, []).map((s) => ({ ...s, t: s.t.replace(/\s+/g, " ").replace(/\s+([,.:])/g, "$1").replace(/\.\./g, ".") }));
}

const SMALL = new Set(["a", "an", "the", "and", "but", "or", "for", "nor", "of", "in", "on", "at", "to", "by", "up", "as", "vs", "via"]);
/** Capitalization advice. The title is never changed: only the writer knows which words are proper nouns. */
export function titleAdvice(style: Style, f: Pick<Fields, "title">): string[] {
  const words = f.title.trim().split(/\s+/).filter(Boolean);
  if (words.length < 3) return [];
  const afterColon = (i: number) => i > 0 && /[:?!]$/.test(words[i - 1]);
  const capped = words.filter((w, i) => i > 0 && !afterColon(i) && /^[A-Z][a-z]/.test(w) && !SMALL.has(w.toLowerCase()));
  const lower = words.filter((w, i) => i > 0 && !SMALL.has(w.toLowerCase()) && /^[a-z]/.test(w));
  if (style === "APA" || style === "Harvard") {
    return capped.length >= 2 && lower.length === 0 && style === "APA"
      ? ["APA 7 writes titles of articles, chapters, books and web pages in sentence case: capitalize only the first word, the first word after a colon, and proper nouns. The title was used as typed."]
      : [];
  }
  return lower.length >= 2 && capped.length === 0
    ? [`${style} writes titles in headline style: capitalize the first and last words and every major word. The title was used as typed.`]
    : [];
}
